import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled, { keyframes, useTheme } from "styled-components";
import { useTranslation } from "react-i18next";

import { Box, Flex, Text, Icon } from "dash-ui";

import { useQuickTour } from "./QuickTourContext";

export type QuickTourPlacement = "top" | "bottom" | "left" | "right";

export interface QuickTourStep {
  resolveTarget: () => HTMLElement | null;
  title: string;
  description: string;
  placement?: QuickTourPlacement;
  spotlightPadding?: number;
  key?: string;
}

interface QuickTourProps {
  isOpen: boolean;
  steps: QuickTourStep[];
  onClose: () => void;
}

const DEFAULT_SPOTLIGHT_PADDING = 8;
const TOOLTIP_GAP = 16;
const TOOLTIP_TOP_EXTRA_GAP = 24;
const TOOLTIP_WIDTH = 320;
const TOOLTIP_HEIGHT_ESTIMATE = 200;
const VIEWPORT_MARGIN = 16;
const SCROLL_BREATHING_ROOM = 50;
const TARGET_RETRY_INTERVAL_MS = 100;
const TARGET_MAX_WAIT_MS = 1500;
const Z_INDEX = 1100;

const isFixedOrSticky = (el: HTMLElement): boolean => {
  let node: HTMLElement | null = el;
  while (node) {
    const pos = getComputedStyle(node).position;
    if (pos === "fixed" || pos === "sticky") return true;
    node = node.parentElement;
  }
  return false;
};

const scrollTargetIntoView = (
  el: HTMLElement,
  placement: QuickTourPlacement
) => {
  // Fixed/sticky targets don't move with page scroll, so scrolling would only
  // shift surrounding content without bringing the target into view.
  if (isFixedOrSticky(el)) return;

  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight;
  const requiredSpace =
    TOOLTIP_GAP +
    TOOLTIP_HEIGHT_ESTIMATE +
    VIEWPORT_MARGIN +
    SCROLL_BREATHING_ROOM;
  let delta = 0;

  if (placement === "bottom") {
    // If target is above (or partially above) the viewport, scrolling up to
    // expose it: center it vertically so surrounding context stays visible.
    // If target overflows the tooltip area below: scroll the minimum needed.
    const maxAllowedBottom = vh - requiredSpace;
    if (rect.top < VIEWPORT_MARGIN) {
      delta = rect.top + rect.height / 2 - vh / 2;
    } else if (rect.bottom > maxAllowedBottom) {
      delta = rect.bottom - maxAllowedBottom;
    }
  } else if (placement === "top") {
    const minAllowedTop = requiredSpace;
    if (rect.bottom > vh - VIEWPORT_MARGIN) {
      delta = rect.top + rect.height / 2 - vh / 2;
    } else if (rect.top < minAllowedTop) {
      delta = rect.top - minAllowedTop;
    }
  } else {
    // For left/right placement, center the target vertically.
    delta = rect.top + rect.height / 2 - vh / 2;
  }

  if (Math.abs(delta) > 4) {
    window.scrollBy({ top: delta, behavior: "smooth" });
  }
};

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

const resolveActualPlacement = (
  rect: DOMRect,
  preferred: QuickTourPlacement
): QuickTourPlacement => {
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const fits: Record<QuickTourPlacement, boolean> = {
    bottom: vh - rect.bottom > TOOLTIP_HEIGHT_ESTIMATE + TOOLTIP_GAP,
    top: rect.top > TOOLTIP_HEIGHT_ESTIMATE + TOOLTIP_GAP,
    right: vw - rect.right > TOOLTIP_WIDTH + TOOLTIP_GAP,
    left: rect.left > TOOLTIP_WIDTH + TOOLTIP_GAP,
  };
  if (fits[preferred]) return preferred;
  const order: QuickTourPlacement[] = ["bottom", "top", "right", "left"];
  return order.find((p) => fits[p]) ?? preferred;
};

const computeTooltipPosition = (
  rect: DOMRect,
  placement: QuickTourPlacement,
  padding: number,
  tooltipHeight: number
): { top: number; left: number; overlapsTarget: boolean } => {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // When neither side of the target has room (a tall modal on a short phone
  // screen), keep the tooltip on-screen even if it has to overlap the target:
  // the overlay blocks the page, so off-screen buttons left no way out.
  const keepOnScreen = (top: number) => {
    const onScreenTop = clamp(top, 8, vh - tooltipHeight - 8);
    return {
      top: onScreenTop,
      overlapsTarget:
        onScreenTop < rect.bottom + padding &&
        onScreenTop + tooltipHeight > rect.top - padding,
    };
  };

  switch (placement) {
    case "bottom":
      return {
        ...keepOnScreen(rect.bottom + padding + TOOLTIP_GAP),
        left: clamp(cx - TOOLTIP_WIDTH / 2, 8, vw - TOOLTIP_WIDTH - 8),
      };
    case "top":
      return {
        ...keepOnScreen(
          rect.top -
            padding -
            TOOLTIP_GAP -
            TOOLTIP_TOP_EXTRA_GAP -
            TOOLTIP_HEIGHT_ESTIMATE
        ),
        left: clamp(cx - TOOLTIP_WIDTH / 2, 8, vw - TOOLTIP_WIDTH - 8),
      };
    case "right":
      return {
        top: clamp(
          cy - TOOLTIP_HEIGHT_ESTIMATE / 2,
          8,
          vh - TOOLTIP_HEIGHT_ESTIMATE - 8
        ),
        left: rect.right + padding + TOOLTIP_GAP,
        overlapsTarget: false,
      };
    case "left":
      return {
        top: clamp(
          cy - TOOLTIP_HEIGHT_ESTIMATE / 2,
          8,
          vh - TOOLTIP_HEIGHT_ESTIMATE - 8
        ),
        left: rect.left - padding - TOOLTIP_GAP - TOOLTIP_WIDTH,
        overlapsTarget: false,
      };
  }
};

const computeCaretStyle = (
  rect: DOMRect,
  tooltipTop: number,
  tooltipLeft: number,
  placement: QuickTourPlacement
): React.CSSProperties => {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const clamp12 = (n: number, max: number) => clamp(n, 12, max - 24);

  switch (placement) {
    case "bottom":
      return { top: -6, left: clamp12(cx - tooltipLeft - 6, TOOLTIP_WIDTH) };
    case "top":
      return { bottom: -6, left: clamp12(cx - tooltipLeft - 6, TOOLTIP_WIDTH) };
    case "right":
      return {
        left: -6,
        top: clamp12(cy - tooltipTop - 6, TOOLTIP_HEIGHT_ESTIMATE),
      };
    case "left":
      return {
        right: -6,
        top: clamp12(cy - tooltipTop - 6, TOOLTIP_HEIGHT_ESTIMATE),
      };
  }
};

const OverlaySvg = styled.svg`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: ${Z_INDEX};
  pointer-events: auto;
`;

const ringPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 3px rgba(63, 139, 231, 0.25),
      0 0 16px 4px rgba(63, 139, 231, 0.35);
  }
  50% {
    box-shadow: 0 0 0 5px rgba(63, 139, 231, 0.18),
      0 0 30px 8px rgba(63, 139, 231, 0.3);
  }
`;

const SpotlightRing = styled.div`
  position: fixed;
  pointer-events: none;
  border: 3px solid ${({ theme }) => theme.colors.b50};
  z-index: ${Z_INDEX + 1};
  animation: ${ringPulse} 2s ease-in-out infinite;
  /* Only ease size — never position. On scroll the box is repositioned from
     fresh getBoundingClientRect() values every frame; easing top/left made the
     ring lag ~150ms behind and visibly drift from the spotlight cutout (which
     has no transition), which is the "highlight moves when scrolling" bug
     (DSH-856). Between steps the ring unmounts, so there's no glide to lose. */
  transition: width 0.15s ease, height 0.15s ease;
`;

const TooltipCard = styled.div`
  position: fixed;
  width: ${TOOLTIP_WIDTH}px;
  max-width: calc(100vw - 16px);
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  padding: 16px;
  z-index: ${Z_INDEX + 1};
`;

const Caret = styled.div<{ $placement: QuickTourPlacement }>`
  position: absolute;
  width: 12px;
  height: 12px;
  background: #ffffff;
  transform: rotate(45deg);
  ${({ $placement }) => {
    switch ($placement) {
      case "bottom":
        return "box-shadow: -2px -2px 4px rgba(0, 0, 0, 0.04);";
      case "top":
        return "box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.04);";
      case "right":
        return "box-shadow: -2px 2px 4px rgba(0, 0, 0, 0.04);";
      case "left":
        return "box-shadow: 2px -2px 4px rgba(0, 0, 0, 0.04);";
    }
  }}
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${({ theme }) => theme.colors.b20};
  color: ${({ theme }) => theme.colors.b50};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
`;

const PrimaryButton = styled.button`
  background: ${({ theme }) => theme.colors.b50};
  color: #ffffff;
  font-weight: 600;
  font-size: 13px;
  padding: 8px 16px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${({ theme }) => theme.colors.b50Hover};
  }
`;

const LinkButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.b50};
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;

  &:hover {
    text-decoration: underline;
  }
`;

function QuickTour({ isOpen, steps, onClose }: QuickTourProps) {
  const theme = useTheme();
  const { t } = useTranslation("pages");
  const { setActiveStepKey } = useQuickTour();
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [placement, setPlacement] = useState<QuickTourPlacement>("bottom");
  const [targetRadius, setTargetRadius] = useState<number>(8);
  const [tooltipHeight, setTooltipHeight] = useState(TOOLTIP_HEIGHT_ESTIMATE);
  const targetRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // The copy decides the real height (the estimate runs short on narrow
  // screens, where the description wraps more), and the on-screen clamp needs
  // the real one. Settles in one extra render; later renders are no-ops.
  useLayoutEffect(() => {
    const height = tooltipRef.current?.offsetHeight;
    if (height && height !== tooltipHeight) setTooltipHeight(height);
  });

  useEffect(() => {
    if (isOpen) {
      setStepIndex(0);
      setTargetRect(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setActiveStepKey(null);
      return;
    }
    setActiveStepKey(steps[stepIndex]?.key ?? null);
  }, [isOpen, stepIndex, steps, setActiveStepKey]);

  useEffect(() => {
    return () => setActiveStepKey(null);
  }, [setActiveStepKey]);

  const advance = useCallback(() => {
    if (stepIndex >= steps.length - 1) {
      onClose();
      return;
    }
    setTargetRect(null);
    setStepIndex((i) => i + 1);
  }, [stepIndex, steps.length, onClose]);

  const goBack = useCallback(() => {
    if (stepIndex === 0) return;
    setTargetRect(null);
    setStepIndex((i) => i - 1);
  }, [stepIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const step = steps[stepIndex];
    if (!step) return;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    let resizeObserver: ResizeObserver | null = null;
    const start = Date.now();
    const preferred = step.placement ?? "bottom";

    const update = () => {
      const target = targetRef.current;
      if (!target || cancelled) return;
      const rect = target.getBoundingClientRect();
      setTargetRect(rect);
      const br = parseFloat(getComputedStyle(target).borderTopLeftRadius);
      setTargetRadius(Number.isFinite(br) && br > 0 ? br : 8);
      setPlacement(resolveActualPlacement(rect, preferred));
    };

    const attach = (el: HTMLElement) => {
      targetRef.current = el;
      scrollTargetIntoView(el, preferred);
      update();
      settleTimer = setTimeout(update, 400);
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(el);
    };

    const tryFind = () => {
      if (cancelled) return;
      const el = step.resolveTarget();
      if (el && el.getBoundingClientRect().height > 0) {
        attach(el);
        return;
      }
      if (Date.now() - start < TARGET_MAX_WAIT_MS) {
        retryTimer = setTimeout(tryFind, TARGET_RETRY_INTERVAL_MS);
      } else if (stepIndex < steps.length - 1) {
        setStepIndex((i) => i + 1);
      } else {
        onClose();
      }
    };

    tryFind();

    const onResizeOrScroll = () => update();
    window.addEventListener("resize", onResizeOrScroll);
    window.addEventListener("scroll", onResizeOrScroll, true);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (settleTimer) clearTimeout(settleTimer);
      resizeObserver?.disconnect();
      targetRef.current = null;
      window.removeEventListener("resize", onResizeOrScroll);
      window.removeEventListener("scroll", onResizeOrScroll, true);
    };
  }, [isOpen, stepIndex, steps, onClose]);

  if (!isOpen) return null;
  const step = steps[stepIndex];
  if (!step) return null;

  const padding = step.spotlightPadding ?? DEFAULT_SPOTLIGHT_PADDING;
  const isLast = stepIndex === steps.length - 1;
  const isFirst = stepIndex === 0;

  let tooltipPos: ReturnType<typeof computeTooltipPosition> | null = null;
  let caretStyle: React.CSSProperties | null = null;
  let spotlight: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null = null;

  if (targetRect) {
    spotlight = {
      x: targetRect.left - padding,
      y: targetRect.top - padding,
      width: targetRect.width + padding * 2,
      height: targetRect.height + padding * 2,
    };
    tooltipPos = computeTooltipPosition(
      targetRect,
      placement,
      padding,
      tooltipHeight
    );
    caretStyle = computeCaretStyle(
      targetRect,
      tooltipPos.top,
      tooltipPos.left,
      placement
    );
  }

  return createPortal(
    <>
      <OverlaySvg>
        <defs>
          <mask id="quick-tour-spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            {spotlight && (
              <rect
                x={spotlight.x}
                y={spotlight.y}
                width={spotlight.width}
                height={spotlight.height}
                rx={8}
                ry={8}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="transparent"
          mask="url(#quick-tour-spotlight-mask)"
        />
      </OverlaySvg>

      {spotlight && (
        <SpotlightRing
          style={{
            top: spotlight.y,
            left: spotlight.x,
            width: spotlight.width,
            height: spotlight.height,
            borderRadius: targetRadius + padding,
          }}
        />
      )}

      {tooltipPos && caretStyle && (
        <TooltipCard
          ref={tooltipRef}
          style={{ top: tooltipPos.top, left: tooltipPos.left }}
        >
          {/* Pushed over its target, the caret would point at nothing. */}
          {!tooltipPos.overlapsTarget && (
            <Caret $placement={placement} style={caretStyle} />
          )}
          <Box mb={4}>
            <Badge>
              {t("quickTour.label")} ·{" "}
              {t("quickTour.progress", {
                current: stepIndex + 1,
                total: steps.length,
              })}
            </Badge>
          </Box>
          <Box mb={2}>
            <Text lg semibold color={theme.colors.gr1}>
              {step.title}
            </Text>
          </Box>
          <Box mb={4}>
            <Text md color={theme.colors.gr3}>
              {step.description}
            </Text>
          </Box>
          <Flex alignItems="center" justifyContent="space-between">
            <LinkButton onClick={onClose}>{t("quickTour.skip")}</LinkButton>
            <Flex alignItems="center" gap={12}>
              {!isFirst && (
                <LinkButton onClick={goBack}>
                  <Icon name="arrowLeft" size={14} color={theme.colors.b50} />
                  {t("quickTour.back")}
                </LinkButton>
              )}
              <PrimaryButton onClick={advance}>
                {isLast ? t("quickTour.done") : t("quickTour.next")}
                {!isLast && <Icon name="arrowRight" size={14} color="white" />}
              </PrimaryButton>
            </Flex>
          </Flex>
        </TooltipCard>
      )}
    </>,
    document.body
  );
}

export default QuickTour;
